import React, { useState } from "react";

import { Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import ToggleSwitch from "components/Switch/ToggleSwitch";
import CustomTooltip from "components/Tooltip/CustomTooltip";
import { TViewMode } from "model/application/modal/CreateEditModal";

import InputBaseLayout from "../InputBaseLayout";
import { IInputBaseLayout } from "../InputBaseLayout/InputBaseLayout";
import InputLabel from "../InputLabel/InputLabel";

const useStyles = makeStyles({
  containerDefault: {
    columnGap: "24px",
  },
  containerWithTooltip: {
    columnGap: "50px",
  },
  toggleBox: {
    opacity: 1,
  },
  toggleBoxDisabled: {
    opacity: 0.5,
  },
});

export interface IInputBooleanProps extends IInputBaseLayout {
  name: string;
  error?: string;
  lang?: {
    title?: string;
    tooltip?: string;
  };
  defaultValue?: boolean;
  onChange: (value: boolean, attrName: string) => void;
  isUndetermined?: boolean;
  viewMode?: TViewMode;
  required?: boolean;
  disabled?: boolean;
  viewStacked?: boolean;
}

export type TInputBoolean = React.FunctionComponent<IInputBooleanProps>;

export const InputBoolean: TInputBoolean = ({
  name,
  error,
  defaultValue,
  isUndetermined,
  lang,
  onChange,
  viewMode = "CREATE",
  required = false,
  viewStacked = false,
  disabled,
  highlightContent = false,
  disabledReason,
  ...rest
}) => {
  const classes = useStyles();
  const [value, setValue] = useState(defaultValue);

  const handleChange = (event: any) => {
    const value = event.target.checked;
    setValue(value);
    onChange(value, name);
  };

  const containerClass =
    required || lang?.tooltip
      ? classes.containerWithTooltip
      : classes.containerDefault;

  const toggleBoxClass = disabled
    ? classes.toggleBoxDisabled
    : classes.toggleBox;

  const InputBooleanCreate = (
    <Box
      display="flex"
      padding="8px 0px 24px 0px"
      className={containerClass}
      lineHeight="1"
      alignItems="center"
      data-testid={rest.dataTestId}
    >
      <InputLabel
        data-input-type="input-boolean"
        title={lang?.title}
        tooltip={viewMode === "CREATE" ? lang?.tooltip : undefined}
        required={required}
        lineHeight="1"
        viewMode={viewMode}
        error={error}
      />
      <Box className={toggleBoxClass}>
        <ToggleSwitch
          name={name}
          defaultChecked={isUndetermined ? false : value}
          disabled={disabled}
          onChange={handleChange}
        />
      </Box>
    </Box>
  );

  if (viewMode === "CREATE") {
    return disabledReason ? (
      <CustomTooltip
        title={disabledReason}
        PopperProps={{
          popperOptions: {
            modifiers: [
              { name: "flip", enabled: false },
              {
                name: "offset",
                options: { offset: [-100, -20] },
              },
            ],
          },
        }}
        placement="top"
      >
        {InputBooleanCreate}
      </CustomTooltip>
    ) : (
      InputBooleanCreate
    );
  }

  return (
    <InputBaseLayout
      disabled={disabled}
      disabledReason={disabledReason}
      dataTestId={rest.dataTestId}
      highlightContent={highlightContent}
      tooltip={lang?.tooltip}
      label={lang?.title}
      required={required}
      viewMode={viewMode}
      error={error}
      viewStacked={viewStacked}
      className="inputBoolean"
    >
      <ToggleSwitch
        name={name}
        defaultChecked={isUndetermined ? false : value}
        onChange={handleChange}
        disabled={disabled || viewMode === "VIEW"}
        dataTestId={rest.dataTestId ? undefined : name}
      />
    </InputBaseLayout>
  );
};

export default InputBoolean;
